import type { BillingPlanHistoryResponseDto } from '../../../../../../../backend/src/organisation/dto/billing-plan-history.response.dto';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useGetOrganisationBillingPlanHistory } from '@/api/queries/useGetOrganisationBillingPlanHistory';
import { SettingsListHeader, SettingsListItem, SettingsSection } from '@/new/components/settings-section';
import { Badge } from '@/new/components/shadcn/badge';
import { formatDatetimeForTable } from '@/new/lib/table-utils';

function formatDate(value: Date | null) {
  return value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Not set';
}

function planSummary(plan: BillingPlanHistoryResponseDto) {
  if (plan.pricingModel === PricingModel.SeatBased) {
    const seats = `${plan.seatCount ?? 'No'} seat${plan.seatCount === 1 ? '' : 's'}`;
    const limit = plan.defaultCreditLimit?.toLocaleString('en-GB') ?? 'No';
    return `Seat-based · ${seats} · ${limit} default credits per user`;
  }

  return `Usage-based · ${plan.maxCredits?.toLocaleString('en-GB') ?? 'No'} maximum credits`;
}

function effectiveRange(plan: BillingPlanHistoryResponseDto) {
  const end = plan.effectiveUntil ? formatDatetimeForTable(plan.effectiveUntil) : 'Present';
  return `${formatDatetimeForTable(plan.effectiveFrom)} – ${end}`;
}

export function BillingPlanHistory({ organisationId }: { organisationId: string }) {
  const { data: plans = [], isLoading, isError } = useGetOrganisationBillingPlanHistory(organisationId);

  return (
    <SettingsSection
      title="Billing plan history"
      description="Every billing plan recorded for this organisation, newest first."
    >
      <SettingsListHeader
        label={
          isLoading
            ? 'Loading billing plans…'
            : isError
              ? 'Billing plans could not be loaded'
              : `${plans.length} billing plan${plans.length === 1 ? '' : 's'}`
        }
      />

      {!isLoading && !isError && plans.length === 0 && (
        <div className="px-5 py-6 text-center text-sm text-gray-500">No billing plans have been recorded.</div>
      )}

      {plans.length > 0 && (
        <div className="max-h-96 divide-y divide-gray-200 overflow-y-auto">
          {plans.map((plan, index) => (
            <SettingsListItem
              key={plan.id}
              leading={<span className="text-sm text-gray-400 tabular-nums">{index + 1}.</span>}
              title={`Billing period · ${formatDate(plan.billingStartDate)} – ${formatDate(plan.billingEndDate)}`}
              description={
                <span>
                  {planSummary(plan)} · Effective {effectiveRange(plan)}
                </span>
              }
              actions={
                plan.effectiveUntil === null ? (
                  <Badge>
                    <span className="mr-1 size-1.25 rounded-full bg-green-500" />
                    Current Plan
                  </Badge>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </SettingsSection>
  );
}
